/**
 * Upper-body motions for the weapons the clips were not made for.
 *
 * The rig only has katana clips. Each weapon here keeps a clip's footwork
 * (and its timing, its warp, its hit frames) and has its arms and torso
 * re-posed every frame by `combat/WeaponMotion.js`: these are the keys it
 * interpolates.
 *
 * A key, at `t` (the move's phase, 0..1 — the same fractions `hits` uses):
 *   r   right hand (wrist), metres in the body's frame: [right, up, forward]
 *   l   left hand, likewise (two-handed weapons put it on the shaft instead)
 *   d   where the weapon points, a direction in the same frame
 *   tw  torso turn, degrees, + to the left
 *   ln  torso lean, degrees, + forward
 * Positions are for a 1.8 m body; they scale with the shoulders.
 *
 * Every move starts and ends on the weapon's `stance`, so moves chain into
 * each other and out to the idle without a pop. Each strike key sits on its
 * move's hit frame, so the blow lands where the weapon is.
 */

const k = (t, r, d, extra = {}) => ({ t, r, d, ...extra });

/* ---- shared shapes for long two-handed weapons ---- */
const HIGH_R = { r: [0.3, 1.65, 0.05], d: [0.25, 0.8, -0.55], tw: -30, ln: -5 };
const HIGH_L = { r: [-0.25, 1.6, 0.1], d: [-0.3, 0.8, -0.5], tw: 30 };
const CUT_DL = { r: [-0.05, 1.02, 0.55], d: [-0.55, -0.38, 0.72], tw: 25, ln: 18 };
const CUT_DR = { r: [0.2, 1.0, 0.55], d: [0.55, -0.4, 0.7], tw: -25, ln: 15 };
const LOW_L = { r: [-0.2, 0.88, 0.35], d: [-0.7, -0.55, 0.45], tw: 35, ln: 10 };
const RISE_R = { r: [0.25, 1.5, 0.45], d: [0.6, 0.65, 0.45], tw: -30, ln: -5 };
const OVER = { r: [0.08, 1.9, 0.0], d: [0, 0.55, -0.85], tw: 0, ln: -15 };
const SLAM = { r: [0.02, 0.88, 0.6], d: [0, -0.65, 0.75], tw: 0, ln: 30 };
const at = (t, pose, extra = {}) => ({ t, ...pose, ...extra });

export const MOTIONS = {
  /* ------------------------------------------------------------ 大太刀 */
  odachi: {
    // Two hands on the long hilt: the left below the right, toward the pommel.
    gap: -0.24,
    stance: { r: [0.1, 1.12, 0.3], d: [-0.05, 0.75, 0.65], tw: 10, ln: 0 },
    guard: { r: [0.05, 1.32, 0.32], d: [-0.85, 0.45, 0.25], tw: 15, ln: 0 },
    moves: {
      // 袈裟斬り — over the right shoulder, down across to the left hip.
      k1: [at(0.3, HIGH_R), at(0.62, CUT_DL), at(0.85, LOW_L)],
      // 逆袈裟 — back up the same line.
      k2: [at(0.2, LOW_L), at(0.45, RISE_R), at(0.75, HIGH_R)],
      // The spin: the blade held out level and carried round by the body.
      k3: [k(0.2, [0.3, 1.2, 0.12], [0.95, 0.05, -0.2], { tw: -40 }), k(0.5, [0.3, 1.2, 0.3], [0.95, 0, 0.3], { tw: 30 }), k(0.8, [-0.1, 1.15, 0.4], [-0.6, 0, 0.8], { tw: 50 })],
      // 十文字 — down-left, then down-right.
      k4: [at(0.15, HIGH_R), at(0.36, CUT_DL), at(0.52, HIGH_L), at(0.72, CUT_DR), at(0.9, { r: [0.3, 0.9, 0.4], d: [0.7, -0.55, 0.4] })],
      // 唐竹割り — the leap, the whole length brought down.
      k5: [at(0.12, OVER), at(0.42, SLAM), at(0.75, SLAM)],
      // 居合 — drawn from low behind, out through the line of the dash.
      heavy: [k(0.25, [0.3, 0.95, -0.1], [0.25, -0.25, -0.95], { tw: -45, ln: 10 }), k(0.66, [-0.15, 1.15, 0.6], [-0.75, 0.05, 0.65], { tw: 40, ln: 10 }), k(0.85, [-0.3, 1.2, 0.35], [-0.9, 0.1, 0.2], { tw: 50 })],
      counter: [at(0.12, HIGH_R), at(0.28, CUT_DL), at(0.45, LOW_L), at(0.66, RISE_R)],
      execute: [at(0.25, OVER, { r: [0.05, 1.95, 0.05] }), at(0.42, SLAM), at(0.8, SLAM)]
    }
  },

  /* ------------------------------------------------------------ 槍 */
  spear: {
    // The right hand at the rear, the left forward on the shaft.
    gap: 0.42,
    stance: { r: [0.2, 1.0, -0.05], d: [-0.15, 0.25, 0.95], tw: -20, ln: 0 },
    guard: { r: [0.15, 1.15, 0.0], d: [-0.5, 0.55, 0.65], tw: -10, ln: 0 },
    moves: {
      // 突き — drawn back to the hip, driven straight out.
      k1: [k(0.35, [0.25, 1.05, -0.3], [-0.05, 0.1, 1], { tw: -30 }), k(0.62, [0.08, 1.25, 0.45], [0, 0.03, 1], { tw: -45, ln: 15 }), k(0.8, [0.08, 1.22, 0.4], [0, 0.03, 1], { tw: -40, ln: 12 })],
      // 上段突き — from low, rising into the face.
      k2: [k(0.22, [0.25, 0.95, -0.25], [0, -0.1, 1], { tw: -30 }), k(0.45, [0.05, 1.45, 0.4], [0, 0.18, 1], { tw: -45, ln: 10 }), k(0.7, [0.1, 1.35, 0.3], [0, 0.15, 1], { tw: -35 })],
      // 薙ぎ — the shaft swung flat through the spin.
      k3: [k(0.2, [0.3, 1.1, 0.1], [0.9, 0.1, 0.4], { tw: -40 }), k(0.5, [0.1, 1.1, 0.35], [0.2, 0.05, 1], { tw: 0 }), k(0.8, [-0.2, 1.1, 0.3], [-0.9, 0.05, 0.4], { tw: 40 })],
      // 二段突き
      k4: [
        k(0.18, [0.25, 1.05, -0.3], [-0.05, 0.08, 1], { tw: -30 }),
        k(0.36, [0.08, 1.25, 0.45], [-0.12, 0.04, 1], { tw: -45, ln: 15 }),
        k(0.54, [0.25, 1.05, -0.25], [0, 0.08, 1], { tw: -30 }),
        k(0.72, [0.06, 1.28, 0.45], [0.12, 0.04, 1], { tw: -48, ln: 18 })
      ],
      // 打ち下ろし — the shaft brought down like a staff.
      k5: [k(0.12, [0.1, 1.85, -0.05], [0, 0.75, 0.65], { ln: -15 }), k(0.42, [0.05, 1.0, 0.45], [0, -0.45, 0.9], { ln: 30 }), k(0.75, [0.05, 1.0, 0.42], [0, -0.42, 0.9], { ln: 25 })],
      // 刺突 — the dash ends in the long thrust.
      heavy: [k(0.3, [0.3, 1.0, -0.4], [0, 0.05, 1], { tw: -35, ln: 10 }), k(0.66, [0.05, 1.25, 0.5], [0, 0, 1], { tw: -50, ln: 20 }), k(0.85, [0.05, 1.25, 0.45], [0, 0, 1], { tw: -45, ln: 15 })],
      counter: [k(0.14, [0.25, 1.05, -0.3], [0, 0.08, 1], { tw: -30 }), k(0.28, [0.08, 1.25, 0.45], [0, 0.04, 1], { tw: -45, ln: 15 }), k(0.48, [0.25, 1.0, -0.25], [0, 0.1, 1], { tw: -30 }), k(0.66, [0.05, 1.4, 0.45], [0, 0.15, 1], { tw: -48, ln: 15 })],
      // Straight down into the fallen.
      execute: [k(0.25, [0.05, 1.7, 0.2], [0, -0.75, 0.65], { ln: -5 }), k(0.42, [0.05, 1.15, 0.4], [0, -0.9, 0.4], { ln: 25 }), k(0.8, [0.05, 1.1, 0.4], [0, -0.9, 0.4], { ln: 25 })]
    }
  },

  /* ------------------------------------------------------------ 薙刀 */
  naginata: {
    gap: 0.42,
    stance: { r: [0.22, 1.0, -0.1], d: [-0.25, 0.55, 0.8], tw: -15, ln: 0 },
    guard: { r: [0.15, 1.2, 0.05], d: [-0.7, 0.6, 0.35], tw: -5, ln: 0 },
    moves: {
      // 薙ぎ払い — low and wide, right to left.
      k1: [k(0.35, [0.35, 1.0, -0.05], [0.85, -0.2, -0.5], { tw: -50 }), k(0.62, [0.1, 1.0, 0.4], [0.05, -0.25, 1], { tw: 0, ln: 10 }), k(0.85, [-0.25, 1.0, 0.3], [-0.9, -0.2, 0.35], { tw: 50 })],
      // 切り上げ
      k2: [k(0.22, [-0.2, 0.9, 0.3], [-0.8, -0.4, 0.45], { tw: 40 }), k(0.45, [0.25, 1.4, 0.35], [0.65, 0.55, 0.5], { tw: -35 }), k(0.75, [0.3, 1.6, 0.1], [0.4, 0.85, -0.3], { tw: -40 })],
      // 回転斬り — the blade at full length round the spin.
      k3: [k(0.2, [0.35, 1.15, 0.1], [0.95, 0.1, 0.25], { tw: -40 }), k(0.5, [0.3, 1.1, 0.25], [0.95, 0, 0.3], { tw: 20 }), k(0.8, [-0.2, 1.1, 0.3], [-0.9, 0, 0.4], { tw: 50 })],
      // 八の字 — a figure of eight.
      k4: [at(0.18, HIGH_R), at(0.36, CUT_DL), at(0.54, HIGH_L), at(0.72, CUT_DR), at(0.9, { r: [0.3, 0.9, 0.35], d: [0.75, -0.5, 0.4] })],
      k5: [at(0.12, OVER), at(0.42, SLAM), at(0.75, SLAM)],
      // The dash's wide sweep.
      heavy: [k(0.3, [0.3, 0.95, -0.2], [0.4, -0.3, -0.85], { tw: -50 }), k(0.66, [-0.1, 1.05, 0.45], [-0.75, -0.15, 0.65], { tw: 40 }), k(0.85, [-0.3, 1.05, 0.3], [-0.95, -0.1, 0.2], { tw: 55 })],
      counter: [at(0.12, LOW_L), at(0.28, RISE_R), at(0.45, HIGH_R), at(0.66, CUT_DL)],
      execute: [at(0.25, OVER), at(0.42, SLAM), at(0.8, SLAM)]
    }
  },

  /* ------------------------------------------------------------ 鎖鎌 */
  kusarigama: {
    // One-handed: the sickle in the right, the chain run through the left.
    gap: null,
    chain: true,
    stance: { r: [0.22, 1.1, 0.3], d: [0.05, 0.95, 0.3], l: [-0.18, 1.12, 0.3], tw: 0, ln: 0 },
    guard: { r: [0.1, 1.35, 0.35], d: [-0.4, 0.85, 0.2], l: [-0.15, 1.25, 0.35], tw: 5, ln: 0 },
    moves: {
      // 掻き斬り — hooked down from high.
      k1: [k(0.35, [0.35, 1.6, 0.05], [0.2, 0.95, 0.1], { l: [-0.18, 1.2, 0.25], tw: -30 }), k(0.62, [-0.05, 1.1, 0.55], [-0.3, -0.2, 0.95], { l: [-0.2, 1.15, 0.2], tw: 25, ln: 15 }), k(0.85, [-0.2, 0.95, 0.35], [-0.5, -0.6, 0.6], { l: [-0.2, 1.15, 0.2], tw: 30 })],
      // 逆手 — backhand across.
      k2: [k(0.22, [-0.25, 1.3, 0.3], [-0.6, 0.7, 0.3], { l: [-0.15, 1.15, 0.2], tw: 35 }), k(0.45, [0.35, 1.25, 0.45], [0.5, 0.3, 0.8], { l: [-0.2, 1.15, 0.2], tw: -35 }), k(0.7, [0.4, 1.2, 0.3], [0.6, 0.4, 0.5], { l: [-0.2, 1.15, 0.2], tw: -35 })],
      // 分銅回し — the weight swung round overhead (see WeaponMotion's chain).
      k3: [k(0.15, [0.2, 1.25, 0.3], [0, 0.95, 0.3], { l: [-0.1, 1.75, 0.2] }), k(0.85, [0.2, 1.25, 0.3], [0, 0.95, 0.3], { l: [-0.1, 1.75, 0.2] })],
      k4: [
        k(0.18, [0.35, 1.6, 0.05], [0.2, 0.95, 0.1], { l: [-0.18, 1.2, 0.25], tw: -30 }),
        k(0.36, [-0.05, 1.1, 0.55], [-0.3, -0.2, 0.95], { l: [-0.2, 1.15, 0.2], tw: 25, ln: 15 }),
        k(0.54, [-0.25, 1.3, 0.3], [-0.6, 0.7, 0.3], { l: [-0.15, 1.15, 0.2], tw: 35 }),
        k(0.72, [0.35, 1.25, 0.45], [0.5, 0.3, 0.8], { l: [-0.2, 1.15, 0.2], tw: -35 })
      ],
      k5: [k(0.12, [0.1, 1.9, 0.05], [0, 0.6, -0.8], { l: [-0.2, 1.4, 0.2], ln: -15 }), k(0.42, [0.05, 0.9, 0.6], [0, -0.6, 0.8], { l: [-0.2, 1.1, 0.3], ln: 30 }), k(0.75, [0.05, 0.9, 0.55], [0, -0.6, 0.8], { l: [-0.2, 1.1, 0.3], ln: 25 })],
      // 分銅投げ — the left arm winds back and throws the weight at the target.
      heavy: [k(0.35, [0.2, 1.2, 0.25], [0, 0.95, 0.3], { l: [-0.35, 1.45, -0.3], tw: 25 }), k(0.55, [0.2, 1.2, 0.3], [0, 0.95, 0.3], { l: [-0.05, 1.4, 0.55], tw: -25, ln: 10 }), k(0.85, [0.2, 1.15, 0.3], [0, 0.95, 0.3], { l: [-0.15, 1.25, 0.35], tw: -10 })],
      counter: [k(0.12, [0.35, 1.6, 0.05], [0.2, 0.95, 0.1], { l: [-0.18, 1.2, 0.25], tw: -30 }), k(0.28, [-0.05, 1.1, 0.55], [-0.3, -0.2, 0.95], { l: [-0.2, 1.15, 0.2], tw: 25, ln: 15 }), k(0.45, [-0.25, 1.3, 0.3], [-0.6, 0.7, 0.3], { l: [-0.15, 1.15, 0.2], tw: 35 }), k(0.66, [0.35, 1.25, 0.45], [0.5, 0.3, 0.8], { l: [-0.2, 1.15, 0.2], tw: -35 })],
      execute: [k(0.25, [0.1, 1.9, 0.1], [0, 0.6, -0.8], { l: [-0.2, 1.3, 0.2], ln: -10 }), k(0.42, [0.05, 0.95, 0.55], [0, -0.7, 0.7], { l: [-0.2, 1.1, 0.3], ln: 30 }), k(0.8, [0.05, 0.95, 0.55], [0, -0.7, 0.7], { l: [-0.2, 1.1, 0.3], ln: 30 })]
    },
    /** What the chain's weight does during each move (WeaponMotion). */
    chainMoves: {
      k3: { type: 'spin', from: 0.12, to: 0.9, turns: 2.5, radius: 2.4 },
      heavy: { type: 'throw', out: 0.5, hit: 0.62, back: 0.95 }
    }
  },

  /* ------------------------------------------------------------ 手甲 */
  gauntlet: {
    // Fists: both hands placed, no weapon to aim.
    gap: null,
    stance: { r: [0.38, 0.85, 0.06], l: [-0.28, 1.05, 0.18], tw: 0, ln: 0 },
    guard: { r: [0.07, 1.5, 0.3], l: [-0.07, 1.52, 0.3], tw: 0, ln: 5 },
    moves: {
      // 刻み突き — the lead (left) jab.
      k1: [k(0.45, [0.12, 1.38, 0.25], null, { l: [-0.12, 1.42, 0.25], tw: -5 }), k(0.62, [0.12, 1.4, 0.25], null, { l: [-0.04, 1.48, 0.7], tw: -25, ln: 8 }), k(0.8, [0.12, 1.38, 0.28], null, { l: [-0.1, 1.44, 0.4], tw: -15 })],
      // 逆突き — the right straight.
      k2: [k(0.25, [0.15, 1.38, 0.12], null, { l: [-0.12, 1.42, 0.32], tw: 10 }), k(0.45, [0.0, 1.45, 0.72], null, { l: [-0.15, 1.4, 0.2], tw: -35, ln: 10 }), k(0.7, [0.05, 1.42, 0.5], null, { l: [-0.15, 1.4, 0.25], tw: -25 })],
      // The kick: the fists stay up in the guard.
      k3: [k(0.5, [0.1, 1.45, 0.25], null, { l: [-0.1, 1.48, 0.28] })],
      // 鉤突き → 突き上げ — left hook, right uppercut.
      k4: [
        k(0.2, [0.12, 1.4, 0.28], null, { l: [-0.38, 1.4, 0.2], tw: 25 }),
        k(0.36, [0.12, 1.4, 0.25], null, { l: [0.05, 1.45, 0.55], tw: -20, ln: 5 }),
        k(0.55, [0.2, 0.95, 0.3], null, { l: [-0.12, 1.42, 0.3], tw: 10, ln: 10 }),
        k(0.72, [0.02, 1.75, 0.5], null, { l: [-0.12, 1.42, 0.3], tw: -20, ln: -10 })
      ],
      // 鉄槌 — both fists brought down on the landing.
      k5: [k(0.12, [0.08, 1.95, 0.05], null, { l: [-0.08, 1.95, 0.05], ln: -15 }), k(0.42, [0.08, 0.6, 0.6], null, { l: [-0.08, 0.6, 0.6], ln: 40 }), k(0.75, [0.08, 0.65, 0.55], null, { l: [-0.08, 0.65, 0.55], ln: 35 })],
      // 体当たり — shoulder first through the dash, a straight at the end.
      heavy: [k(0.3, [0.1, 1.3, 0.15], null, { l: [-0.18, 1.4, 0.15], tw: -40, ln: 15 }), k(0.66, [0.0, 1.45, 0.75], null, { l: [-0.18, 1.4, 0.15], tw: -40, ln: 15 }), k(0.85, [0.05, 1.42, 0.5], null, { l: [-0.15, 1.4, 0.2], tw: -30 })],
      counter: [k(0.12, [0.4, 1.4, 0.2], null, { l: [-0.12, 1.42, 0.3], tw: -25 }), k(0.28, [-0.05, 1.45, 0.5], null, { l: [-0.12, 1.42, 0.3], tw: 20, ln: 5 }), k(0.48, [0.12, 1.4, 0.28], null, { l: [-0.2, 0.95, 0.3], tw: -10, ln: 10 }), k(0.66, [0.12, 1.4, 0.28], null, { l: [-0.02, 1.75, 0.5], tw: 20, ln: -10 })],
      execute: [k(0.25, [0.08, 1.95, 0.05], null, { l: [-0.08, 1.95, 0.05], ln: -15 }), k(0.42, [0.08, 0.65, 0.6], null, { l: [-0.08, 0.65, 0.6], ln: 40 }), k(0.8, [0.08, 0.65, 0.6], null, { l: [-0.08, 0.65, 0.6], ln: 40 })]
    }
  },

  /* ------------------------------------------------------------ 手裏剣 */
  shuriken: {
    gap: null,
    stance: { r: [0.25, 1.15, 0.2], l: [-0.15, 1.25, 0.28], tw: 0, ln: 0 },
    guard: { r: [0.12, 1.35, 0.3], l: [-0.12, 1.4, 0.3], tw: 0, ln: 0 },
    moves: {
      // 上手打ち — from beside the ear.
      s1: [k(0.35, [0.3, 1.65, -0.2], null, { tw: -30, ln: -5 }), k(0.6, [0.02, 1.4, 0.6], null, { tw: 15, ln: 10 }), k(0.8, [-0.1, 1.2, 0.5], null, { tw: 15, ln: 5 })],
      // 逆手打ち — the backhand flick.
      s2: [k(0.35, [-0.3, 1.35, 0.15], null, { tw: 35 }), k(0.6, [0.35, 1.35, 0.55], null, { tw: -25 }), k(0.8, [0.4, 1.3, 0.4], null, { tw: -25 })],
      // 横打ち — side-arm, the fan of three.
      s3: [k(0.35, [0.45, 1.25, -0.05], null, { tw: -45 }), k(0.6, [-0.1, 1.3, 0.6], null, { tw: 25, ln: 5 }), k(0.8, [-0.25, 1.25, 0.45], null, { tw: 30 })],
      // 両手打ち — both hands, five stars.
      heavy: [k(0.35, [0.35, 1.5, -0.1], null, { l: [-0.35, 1.5, -0.1], ln: -5 }), k(0.6, [0.25, 1.4, 0.6], null, { l: [-0.25, 1.4, 0.6], ln: 10 }), k(0.8, [0.25, 1.3, 0.45], null, { l: [-0.25, 1.3, 0.45], ln: 5 })],
      counter: [k(0.35, [0.45, 1.25, -0.05], null, { tw: -45 }), k(0.6, [-0.1, 1.3, 0.6], null, { tw: 25, ln: 5 })],
      // 手刀 — the knife-hand on a broken body.
      execute: [k(0.25, [0.15, 1.9, 0.05], null, { l: [-0.15, 1.3, 0.3], ln: -10 }), k(0.42, [0.05, 0.85, 0.6], null, { l: [-0.15, 1.2, 0.3], ln: 35 }), k(0.8, [0.05, 0.85, 0.6], null, { l: [-0.15, 1.2, 0.3], ln: 35 })]
    }
  }
};

// These keys share the aerial controller's contact phase (0.5) and landing phase (1).
MOTIONS.gauntlet.moves.heavy = [k(0.3, [0.18, 0.95, 0.25], null, { l: [-0.12, 1.4, 0.3], ln: 12 }), k(0.62, [0.02, 1.85, 0.55], null, { l: [-0.12, 1.4, 0.3], tw: -20, ln: -8 }), k(0.9, [0.12, 1.4, 0.3], null, { l: [-0.12, 1.4, 0.3] })];
MOTIONS.gauntlet.moves.k3 = [k(0.25, [0.35, 1.35, 0.12], null, { tw: -25 }), k(0.55, [-0.02, 1.45, 0.65], null, { tw: 25, ln: 8 }), k(0.85, [0.12, 1.4, 0.3], null, { tw: 0 })];
MOTIONS.gauntlet.moves.air1 = [k(0, [0.12, 1.4, 0.28], null, { l: [-0.12, 1.4, 0.25] }), k(0.5, [0.12, 1.4, 0.25], null, { l: [-0.02, 1.5, 0.75], tw: -20 }), k(1, [0.12, 1.4, 0.28], null, { l: [-0.12, 1.4, 0.3] })];
MOTIONS.gauntlet.moves.air2 = [k(0, [0.15, 1.4, 0.15], null, { tw: 15 }), k(0.5, [0, 1.5, 0.75], null, { tw: -25 }), k(1, [0.12, 1.4, 0.3], null, { tw: 0 })];
MOTIONS.gauntlet.moves.airSlam = [k(0, [0.1, 1.9, 0.1], null, { l: [-0.1, 1.9, 0.1], ln: -10 }), k(1, [0.1, 0.75, 0.55], null, { l: [-0.1, 0.75, 0.55], ln: 30 })];
